import { motion } from "framer-motion";
import { TrendingUp, Users, Clock, ArrowUpRight, Star, UtensilsCrossed } from "lucide-react";
import { Link } from "react-router-dom";

const caseStudies = [
  {
    client: "INSD",
    hash: "insd",
    industry: "Education",
    results: [
      { icon: Users, label: "62% increase in qualified applications" },
      { icon: TrendingUp, label: "3x inquiry-to-enrollment rate" },
    ],
    description: "Implemented a full-funnel lead generation system with automated follow-ups across 3 locations, turning website visitors into enrolled students.",
    review: {
      quote: "TheGrowthBy transformed our student acquisition across all 3 centers. Their integrated funnel approach gave us consistency and visibility we never had before.",
      name: "Varsha Chandwani",
      role: "Founder, INSD",
    },
  },
  {
    client: "M.D. Eye Care & Laser Centre",
    hash: "dental",
    industry: "Professional Services",
    results: [
      { icon: TrendingUp, label: "3x increase in inbound leads" },
      { icon: Clock, label: "75% faster response time" },
    ],
    description: "Built an integrated lead capture and booking system across 2 locations with instant follow-up, dramatically reducing lost opportunities.",
    review: {
      quote: "TheGrowthBy helped us streamline patient acquisition across both our locations. The integrated system gave us control and visibility we didn't have before.",
      name: "Dr Alkesh Chaudhary",
      role: "Founder, M.D. Eye Care & Laser Centre",
    },
  },
  {
    client: "Flaming Tikkas",
    hash: "flaming-tikkas",
    industry: "Restaurants",
    results: [
      { icon: UtensilsCrossed, label: "40% increase in repeat orders" },
      { icon: TrendingUp, label: "2.5x direct online orders" },
    ],
    description: "Launched targeted campaigns with automated follow-ups and QR-based customer capture to increase orders and bring customers back across all locations.",
    review: {
      quote: "TheGrowthBy helped us take control of our customer relationships across all locations. The repeat order system alone has been a game changer.",
      name: "Rohit Gupta",
      role: "Founder, Flaming Tikkas",
    },
  },
];

const CaseStudiesSection = () => {
  return (
    <section className="py-24 px-4 bg-secondary">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-display-md font-bold text-foreground mb-4">Real Results</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            See how we've helped businesses like yours grow.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {caseStudies.map((study, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              className="flex flex-col gap-6"
            >
              {/* Case Study Card */}
              <Link
                to={`/case-studies#${study.hash}`}
                className="bg-card border border-border rounded-2xl p-8 hover:shadow-lg hover:border-primary/30 transition-all duration-300 group block"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-foreground">{study.client}</h3>
                    <span className="text-xs font-medium text-muted-foreground bg-secondary px-3 py-1 rounded-full">
                      {study.industry}
                    </span>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">{study.description}</p>
                <div className="space-y-3">
                  {study.results.map((result, j) => (
                    <div key={j} className="flex items-center gap-3 p-3 rounded-lg bg-primary/5">
                      <result.icon className="h-4 w-4 text-primary" />
                      <span className="text-sm font-semibold text-foreground">{result.label}</span>
                    </div>
                  ))}
                </div>
              </Link>

              {/* Review Card */}
              <Link
                to={`/case-studies#${study.hash}`}
                className="bg-card border border-border rounded-2xl p-6 hover:shadow-lg hover:border-primary/30 transition-all duration-300 block"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed italic">
                  "{study.review.quote}"
                </p>
                <div>
                  <p className="text-sm font-semibold text-foreground">{study.review.name}</p>
                  <p className="text-xs text-muted-foreground">{study.review.role}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesSection;
